import { useEffect, useState } from "react";
import { LocalPriceResponse, Money, PaymentOption, StringIndexableDictionary } from "../../../Common/Types";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { AppConfig } from "../../../AppConfig";
import { JWT_API_KEY } from "../../../Common/Constants";
import { GenerateHeaderInformation } from "../../../Common/Helpers/ApiHelper";
import { FormatCurrency, FormattedMoney } from "../../../Common/Helpers/MoneyHelper";
import { fetchRetry } from "../../../Common/services/FetchRetry";
import { returnJsonOrThrowError } from "../../../Common/services/ErrorService";
import Enrich from "../../../Common/services/TextEnricher";

export function useLocalCurrencyPayment({
  quoteId,
  jwtAccessToken,
  paymentValue,
  chosenPaymentOption,
  hasDiscount,
  discountedPrice,
  labels,
  isEnabled,
}: {
  quoteId: string;
  jwtAccessToken: string;
  paymentValue: Money;
  chosenPaymentOption: PaymentOption;
  hasDiscount: boolean;
  discountedPrice: Money;
  labels: StringIndexableDictionary<string>;
  isEnabled: boolean;
}) {
  const [localPrice, setLocalPrice] = useState<LocalPriceResponse | undefined>(undefined);
  const [payInLocalCurrency, setPayInLocalCurrency] = useState(false);
  const [isLoadingLocalPrice, setIsLoadingLocalPrice] = useState(isEnabled);

  useEffect(() => {
    if (!quoteId || !isEnabled) return;
    setIsLoadingLocalPrice(true);
    fetchRetry(`${AppConfig.api.quotation}/quotes/${quoteId}/local-price`, {
      method: "GET",
      headers: GenerateHeaderInformation(jwtAccessToken, JWT_API_KEY),
    })
      .then((response) => (response.status === 404 ? undefined : returnJsonOrThrowError<LocalPriceResponse>(response)))
      .then((response) => setLocalPrice(response))
      .catch((error) => {
        console.error("[Payment] getQuoteLocalPrice error", error);
        setLocalPrice(undefined);
      })
      .finally(() => setIsLoadingLocalPrice(false));
  }, [quoteId, jwtAccessToken, isEnabled]);

  const canOfferLocalCurrency = isEnabled && !!localPrice;

  const localAmountForCurrentSelection = localPrice
    ? chosenPaymentOption == PaymentOption.FullPayment
      ? hasDiscount
        ? localPrice.totalPriceWithDiscount
        : localPrice.totalPrice
      : localPrice.reservationPrice
    : undefined;

  const displayValue: Money =
    payInLocalCurrency && localPrice && localAmountForCurrentSelection !== undefined
      ? { amount: localAmountForCurrentSelection, currency: localPrice.currency }
      : paymentValue;

  const localAmounts =
    payInLocalCurrency && localPrice
      ? {
          totalPrice: { amount: localPrice.totalPrice, currency: localPrice.currency },
          discountedPrice: { amount: localPrice.totalPriceWithDiscount, currency: localPrice.currency },
          reservationPrice: { amount: localPrice.reservationPrice, currency: localPrice.currency },
        }
      : undefined;

  const totalDisplayValue: Money = discountedPrice;

  const localDiscountAmount = localPrice ? localPrice.totalPrice - localPrice.totalPriceWithDiscount : 0;

  const exchangeRateText = localPrice
    ? `1 USD = ${FormatCurrency(localPrice.appliedExchangeRate, localPrice.currency, 2)}`
    : "";

  const disclaimerText = payInLocalCurrency
    ? Enrich(labels[LabelKey.paymentCurrencyDisclaimer_local], { exchangeRateText })
    : labels[LabelKey.paymentCurrencyDisclaimer_usd];

  const installmentsDisclaimerText =
    payInLocalCurrency && localPrice ? labels[LabelKey.paymentCurrencyDisclaimer_installments] : undefined;

  return {
    localPrice,
    payInLocalCurrency,
    setPayInLocalCurrency,
    canOfferLocalCurrency,
    isLoadingLocalPrice,
    localAmountForCurrentSelection,
    displayValue,
    formattedDisplayValue: FormattedMoney(displayValue),
    localAmounts,
    totalDisplayValue,
    localDiscountAmount,
    disclaimerText,
    installmentsDisclaimerText,
  };
}
